import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import ExpiryDatePicker from './ExpiryDatePicker';
import { PlusIcon, BookOpenIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface LectioDocument {
  id: string;
  title: string;
  date: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  lastModified: string;
}

const LectioManager: React.FC = () => {
  const [documents, setDocuments] = useState<LectioDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<LectioDocument | null>(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    file: null as File | null,
    expiryDate: ''
  });

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const data = await apiService.getLectioDivina();
      const documents = Array.isArray(data) ? data.filter(item => item !== null) as unknown as LectioDocument[] : [];
      setDocuments(documents);
    } catch (error) {
      console.error('Failed to load lectio divina documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Accept PDF, DOC, DOCX files
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a PDF, DOC, or DOCX file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      setUploadForm(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!uploadForm.file || !uploadForm.title || !uploadForm.date) {
      alert('Please fill in all required fields and select a file');
      return;
    }

    setUploading(true);
    try {
      await apiService.uploadLectioDivina(
        uploadForm.file,
        uploadForm.title,
        uploadForm.description,
        uploadForm.date,
        uploadForm.expiryDate || undefined
      );

      // Reset form
      setUploadForm({
        title: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        file: null,
        expiryDate: ''
      });
      
      // Clear file input
      const fileInput = document.getElementById('lectio-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      // Reload documents
      await loadDocuments();
      
      alert('Lectio Divina document uploaded successfully!');
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (document: LectioDocument) => {
    setDeleteConfirm(document);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await apiService.deleteLectioDivina(deleteConfirm.id);
      await loadDocuments();
      setDeleteConfirm(null);
      alert('Document deleted successfully');
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete document. Please try again.');
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <PlusIcon className="h-5 w-5 mr-2" />
          Upload Lectio Divina Document
        </h3>
        
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                id="title"
                value={uploadForm.title}
                onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
                placeholder="e.g., March 2024 Lectio Divina Guide"
                required
              />
            </div>
            
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                id="date"
                value={uploadForm.date}
                onChange={(e) => setUploadForm(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
                required
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={uploadForm.description}
              onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
              rows={3}
              placeholder="Optional description of the document content"
            />
          </div>
          
          <div>
            <label htmlFor="lectio-file" className="block text-sm font-medium text-gray-700 mb-1">
              Document File * (PDF, DOC, DOCX - Max 10MB)
            </label>
            <input
              type="file"
              id="lectio-file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
            {uploadForm.file && (
              <p className="text-sm text-gray-600 mt-1">
                Selected: {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)})
              </p>
            )}
          </div>

          <ExpiryDatePicker
            value={uploadForm.expiryDate}
            onChange={(expiryDate) => setUploadForm(prev => ({ ...prev, expiryDate }))}
          />

          <button
            type="submit"
            disabled={uploading}
            className="bg-church-blue text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-church-blue disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Uploading...' : 'Upload Document'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <BookOpenIcon className="h-5 w-5 mr-2" />
          Existing Documents
        </h3>
        
        {loading ? (
          <p className="text-gray-600">Loading documents...</p>
        ) : documents.length === 0 ? (
          <p className="text-gray-600">No documents uploaded yet.</p>
        ) : (
          <div className="space-y-3">
            {documents.map((document) => (
              <div key={document.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{document.title}</h4>
                  <p className="text-sm text-gray-600">
                    {document.fileName} • {formatFileSize(document.fileSize)} • 
                    {new Date(document.date).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <a
                    href={document.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-church-blue hover:text-blue-700 text-sm font-medium"
                  >
                    View Document
                  </a>
                  <button
                    onClick={() => handleDelete(document)}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Delete document"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <XMarkIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Deletion
              </h3>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete "{deleteConfirm.title}"? This action cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded transition-colors duration-200"
              >
                Delete
              </button>
              <button
                onClick={cancelDelete}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LectioManager;